import { useState, useCallback } from 'react';

interface UseEditableCardOptions<T extends Record<string, unknown>> {
  initialData: T;
  onSave: (data: T) => Promise<void>;
  onSaved?: () => void;
}

interface UseEditableCardReturn<T extends Record<string, unknown>> {
  isEditing: boolean;
  formData: T;
  isSaving: boolean;
  error: string | null;
  startEditing: () => void;
  cancelEditing: () => void;
  updateField: <K extends keyof T>(key: K, value: T[K]) => void;
  save: () => Promise<void>;
}

export function useEditableCard<T extends Record<string, unknown>>({
  initialData,
  onSave,
  onSaved,
}: UseEditableCardOptions<T>): UseEditableCardReturn<T> {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<T>(initialData);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEditing = useCallback(() => {
    setFormData(initialData);
    setError(null);
    setIsEditing(true);
  }, [initialData]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setError(null);
  }, []);

  const updateField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const save = useCallback(async () => {
    try {
      setIsSaving(true);
      setError(null);
      await onSave(formData);
      setIsEditing(false);
      onSaved?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erreur lors de la sauvegarde'
      );
    } finally {
      setIsSaving(false);
    }
  }, [formData, onSave, onSaved]);

  return {
    isEditing,
    formData,
    isSaving,
    error,
    startEditing,
    cancelEditing,
    updateField,
    save,
  };
}
